import _ from "lodash";
import { ROLES } from "@/utils/users";
import {
  REFERENCIAL_APPLICATION_NAME,
  COUNTRY_WITH_DIAGNOSTIC_REPORTS,
  COUNTRY_WITH_RD_REPORTS,
  FR_AGENCIES_WITH_NO_QUESTIONNAIRE,
  REFERENCIAL_NAME_BY_COUNTRY,
  GRPC_PARAMS,
} from "@/utils/sections";
import {
  COUNTRY_CODE,
  CALEDONIE_AGENCY_START_POSTAL_CODE,
  POLYNESIE_CASE_START_CODE,
  DOM_START_POSTAL_CODE,
} from "@/utils/localisation";

import moment from "moment";
import { ENTITY_TYPES } from "@/utils/project_enums";
import ProjectRole from "@/models/ProjectRole";
import Project from "@/models/Project";
import { BIMDATA_STATUS } from "@/constants";
import { defineStore } from "pinia";
import { useUserStore } from "./user.js";
import { getCollaborationApi, getModelApi } from "@/api/bimdataClient";
import rapsotecClient from "@/api/rapsotecClient";
import RapsoError from "@/models/RapsoError";
import {
  removeCacheForProjectId,
  retrieveCacheKeyForProjectId,
} from "@/setup/cache";
import { useProjectApi } from "@/setup/api";
import { useQuestionnaireStore } from "./questionnaire.js";

const BLUEGEN_CHATBOT_TOKEN_ENDPOINT = "/api/ai-services/bluegen/chatbot/token/";

export const useProjectStore = defineStore("projectStore", {
  state: () => ({
    selectedRapsotecProject: {
      case_number: null,
      address: "",
      city: "",
      webmail_email: "",
      webmail_password: "",
      missions: [],
      entity: "",
      origin_id: "",
      cloud: "",
    },
    unreadMail: 0,
    ifcs: null,
    projectUsers: [],
    currentProjectUser: null,
    bimdataProjectToken: null,
    bimdataState: "",
    dashboardCustomParam: {
      actionPlan: 3,
      "S&D": 3,
    },
  }),
  getters: {
    projectId: (state) => state.selectedRapsotecProject.project_id,

    getSortedIfc: (state) => _.sortBy(state.ifcs, ["updated_at"]),

    rapsotecInternalUsers: (state) =>
      state.projectUsers?.filter((projectUser) =>
        projectUser.user_profile.isFromSocotec(),
      ) ?? [],
    rapsotecExternalUsers: (state) =>
      state.projectUsers?.filter(
        (projectUser) => !projectUser.user_profile.isFromSocotec(),
      ) ?? [],
    rapsotecProjectFormated() {
      return {
        project_name: encodeURI(this.selectedRapsotecProject.project_name),
        project_id: this.selectedRapsotecProject.project_id,
        building_permit_request_date:
          this.selectedRapsotecProject.building_permit_request_date,
        building_permit_acquisition_date:
          this.selectedRapsotecProject.building_permit_acquisition_date,
        case_postcode: this.selectedRapsotecProject.case_postcode,
        ope_start_date: this.selectedRapsotecProject.ope_start_date,
        created_at: moment(this.selectedRapsotecProject.created_at).format(
          "YYYY-MM-DD",
        ),
        agency_country: this.selectedRapsotecProject.agency_country,
        destinations:
          this.selectedRapsotecProject.destinations?.map((destination) => ({
            code: destination,
          })) || [],
        missions:
          this.selectedRapsotecProject.missions?.map((mission) => ({
            code: mission.code,
          })) || [],
        types: this.selectedRapsotecProject.types?.map((type) => ({
          code: type.code,
        })),
        case: {
          agency_country: this.selectedRapsotecProject.agency_country,
          ope_start_date: this.selectedRapsotecProject.ope_start_date,
          case_postcode: this.selectedRapsotecProject.case_postcode,
        },
        user: {
          language: useUserStore().getCurrentUserProfile.language,
        },
      };
    },
    areSomeQuestionnairesCompleted() {
      if (this.selectedRapsotecProject.questionnaires_completed) {
        return Object.values(
          this.selectedRapsotecProject.questionnaires_completed,
        ).includes(true);
      } else {
        return false;
      }
    },
    seeMoroccoFeatures: (state) =>
      state.selectedRapsotecProject.agency_country === COUNTRY_CODE.maroc,
    isBelgiumFeaturesAllowed: (state) =>
      state.selectedRapsotecProject.agency_country === COUNTRY_CODE.belgique,
    isLuxembourgFeaturesAllowed: (state) =>
      state.selectedRapsotecProject.agency_country === COUNTRY_CODE.luxembourg,
    isFranceFeaturesAllowed: (state) =>
      state.selectedRapsotecProject.agency_country === COUNTRY_CODE.france,

    isRDReportsGenerationAllowed: (state) =>
      COUNTRY_WITH_RD_REPORTS.includes(
        state.selectedRapsotecProject.agency_country,
      ),

    isDiagnosticReportsGenerationAllowed: (state) =>
      COUNTRY_WITH_DIAGNOSTIC_REPORTS.includes(
        state.selectedRapsotecProject.agency_country,
      ),
    isSecuProject: (state) =>
      state.selectedRapsotecProject.entity === ENTITY_TYPES.SECU,
    isCTProject: (state) =>
      state.selectedRapsotecProject.entity === ENTITY_TYPES.CT,
    isItmProject: (state) =>
      state.selectedRapsotecProject.entity === ENTITY_TYPES.ITM,
    isBuildingPermitReportGenerationAllowed() {
      return (
        this.selectedRapsotecProject?.contracting_agency?.postal_code?.startsWith(
          CALEDONIE_AGENCY_START_POSTAL_CODE,
        ) &&
        this.selectedRapsotecProject?.missions
          ?.map((mission) => mission.code)
          .includes("S")
      );
    },

    isDomProject() {
      const agencyPostalCode =
        this.selectedRapsotecProject?.contracting_agency?.postal_code;

      if (agencyPostalCode) {
        return DOM_START_POSTAL_CODE.some((domCode) =>
          agencyPostalCode.startsWith(domCode.code),
        );
      }

      return false;
    },

    // C.C 16/07/26 périmètre V4 RICT/RFCT : France métro + DOM + Polynésie ; la Calédonie reste sur les OLD.
    isNewRictRfctScope() {
      const postalCode =
        this.selectedRapsotecProject?.contracting_agency?.postal_code;
      const isCaledonie =
        !!postalCode &&
        postalCode.startsWith(CALEDONIE_AGENCY_START_POSTAL_CODE);
      const isPolynesie =
        !!postalCode && postalCode.startsWith(POLYNESIE_CASE_START_CODE);
      return (
        ((this.selectedRapsotecProject?.agency_country ===
          COUNTRY_CODE.france ||
          this.isDomProject) &&
          !isCaledonie) ||
        isPolynesie
      );
    },

    // INFO - AG - 27/07/2022 - Display questionnaire, when it's configured, when opening a new canvas.
    // This feature is available only for french canvas ( including Reunion, Mayotte and Antille canvas)
    // We do not display questionnaire for other country (first step for BE , LU and CI)
    // We will NOT DISPLAY the questionnaire for TOM (country code FR and Nouvelle Calédonie code postal agency for the moment),
    // see utils/sections.js/ FR_AGENCIES_WITH_NO_QUESTIONNAIRE for implementation of the agency postal code related to TOM (Nouvelle Caledonie)
    isQuestionnaireToDisplay() {
      return (
        this.selectedRapsotecProject.agency_country === COUNTRY_CODE.france &&
        ![FR_AGENCIES_WITH_NO_QUESTIONNAIRE].some((agencyCode) =>
          this.selectedRapsotecProject.contracting_agency.postal_code?.startsWith(
            agencyCode,
          ),
        )
      );
    },
    getReferencialApplicationNameByProjectCountry() {
      return (
        REFERENCIAL_NAME_BY_COUNTRY[
          this.selectedRapsotecProject.agency_country
        ] || REFERENCIAL_APPLICATION_NAME.CT_RAPSOTEC_FRANCE
      );
    },
    getCanvasReferencialApplicationName() {
      if (this.isSecuProject) {
        return REFERENCIAL_APPLICATION_NAME.SECU_RAPSOSECU_FRANCE;
      } else if (this.isItmProject) {
        return REFERENCIAL_APPLICATION_NAME.ITM_RAPSOITM_LUXEMBOURG;
      }
      return this.getReferencialApplicationNameByProjectCountry;
    },
    projectUserByUuid() {
      return (uuid) => {
        return this.projectUsers.find((user) => user.uuid === uuid);
      };
    },
    getMissionsCodes() {
      return (missions) => {
        return missions?.map((missions) => missions.code) || [];
      };
    },
    getProjectMainDestinationCode: (state) => {
      return state.selectedRapsotecProject.main_destination;
    },
    getTypeCodes() {
      return (types) => {
        return types?.map((_type) => _type.code) || [];
      };
    },
    getCurrentProjectUserRole() {
      if (useUserStore().isDevOrSuperUser) {
        return ROLES.DEVELOPER;
      } else {
        return this.currentProjectUser.role;
      }
    },
    getProjectAccessToken() {
      return this.bimdataProjectToken ? this.bimdataProjectToken.token : null;
    },
    justificationsCount() {
      return this.selectedRapsotecProject?.justifications.length || 0;
    },
    getProjectName() {
      return this.selectedRapsotecProject.project_name;
    },
    hasReportsToAnswer() {
      if (this.selectedRapsotecProject.reports_survey) {
        return Object.values(this.selectedRapsotecProject.reports_survey).some(
          (obj) => !obj.answered,
        );
      } else {
        return false;
      }
    },
    getReportUuidsToAnswer() {
      const reports_survey = this.selectedRapsotecProject.reports_survey;
      if (reports_survey) {
        return Object.keys(reports_survey).filter(
          (k) => !reports_survey[k]["answered"],
        );
      } else {
        return [];
      }
    },
    getProjectParams() {
      if (this.isSecuProject) {
        return GRPC_PARAMS.NATIONAL_CURRENT_RAPSOSECU_PARAMS;
      }
      if (this.isItmProject) {
        return GRPC_PARAMS.NATIONAL_CURRENT_RAPSOITM_PARAMS;
      }
      if (this.seeMoroccoFeatures) {
        return GRPC_PARAMS.MOROCCO_CURRENT_PARAMS;
      } else {
        return GRPC_PARAMS.NATIONAL_CURRENT_PARAMS;
      }
    },
    hasNoProjectManager() {
      return !Object.hasOwn(this.selectedRapsotecProject, "project_manager");
    },
  },
  actions: {
    async getRapsotecProject(projectId, force = false) {
      if (this.selectedRapsotecProject.project_id !== projectId || force) {
        const response = await rapsotecClient.get(`project/${projectId}/`);
        this.setRapsotecProject(response.data);

        const application_name = this.getCanvasReferencialApplicationName;
        const local_storage_key = `lastSectionsUpdated.${application_name}`;
        let lastSectionsUpdated = localStorage.getItem(local_storage_key);

        let getProjectLastSectionUpdated =
          await retrieveCacheKeyForProjectId(projectId);

        if (!getProjectLastSectionUpdated || !window.caches) {
          return;
        }

        getProjectLastSectionUpdated = new URL(
          getProjectLastSectionUpdated.url,
        );

        getProjectLastSectionUpdated =
          getProjectLastSectionUpdated.searchParams.get("last_update");

        if (getProjectLastSectionUpdated !== lastSectionsUpdated) {
          await removeCacheForProjectId(projectId);
        }
      }
    },
    async getProjectGrcp(projectId) {
      const response = await useProjectApi().retrieve({ projectId });
      return response;
    },
    async fetchProjectUsers() {
      const { data } = await rapsotecClient.get(
        `/project/${this.selectedRapsotecProject.project_id}/users/`,
      );
      this.projectUsers = data.map(
        (projectUser) => new ProjectRole(projectUser),
      );
    },
    async fetchUnreadMailForCurrentProject() {
      try {
        if (!this.selectedRapsotecProject) {
          throw new Error(
            "Can't fetch unread mail because selected project is not set",
          );
        }
        const {
          data: { unread_mail },
        } = await rapsotecClient.get(
          `/project/${this.selectedRapsotecProject.project_id}/unread-mail`,
        );
        this.unreadMail = unread_mail;
      } catch (err) {
        console.error(
          `Error when fetching unred mail for project ${this.selectedRapsotecProject.id}: `,
          err,
        );
      }
    },
    async fetchProjectIfc() {
      try {
        const modelApi = await getModelApi();
        const cloudId = this.selectedRapsotecProject.cloud;
        const originId = this.selectedRapsotecProject.origin_id;
        const ifcs = await modelApi.getModels(cloudId, originId, {
          type: ["IFC"],
        });
        if (!ifcs) {
          return;
        }
        this.ifcs = ifcs;

        let pendingStatus = ["P", "I"];
        let hasPendingIfc = ifcs.some((ifc) =>
          pendingStatus.includes(ifc.status),
        );

        if (hasPendingIfc) {
          const idInterval = setInterval(async () => {
            const ifcsPending = await modelApi.getModels(cloudId, originId, {
              type: ["IFC"],
              status: pendingStatus,
            });
            if (ifcsPending.length === 0) {
              const ifcs = await modelApi.getModels(cloudId, originId, {
                type: ["IFC"],
              });
              this.ifcs = ifcs;
              clearInterval(idInterval);
            }
          }, 5000);
        }
        return ifcs;
      } catch (e) {
        console.error(e);
      }
    },

    async fetchCurrentProjectUser({ projectId }) {
      const { data } = await rapsotecClient(
        `/project/${projectId}/current-project-role/`,
      );
      // fetchCurrentProjectUser only get information about the use rin the project
      // if we want to use information about user profile we need to get them form the user store
      data.user_profile = useUserStore().getCurrentUserProfile;
      this.currentProjectUser = new ProjectRole(data);
    },

    async createProjectUser(projectUserData) {
      const { data } = await rapsotecClient.post(
        `/project/${this.selectedRapsotecProject.project_id}/users/`,
        projectUserData,
      );
      this.projectUsers.unshift(new ProjectRole(data));
    },

    async updateProjectUser({ projectUserUuid, projectUserData }) {
      const { data } = await rapsotecClient.patch(
        `/project/${this.selectedRapsotecProject.project_id}/users/${projectUserUuid}/`,
        projectUserData,
      );
      this.projectUsers.forEach((item, index) => {
        if (item.uuid === data.uuid) {
          this.projectUsers.splice(index, 1, new ProjectRole(data));
        }
      });
    },

    async updateSelfProjectUser({ projectUserData }) {
      const { data } = await rapsotecClient.patch(
        `/project/${this.selectedRapsotecProject.project_id}/current-project-role/`,
        projectUserData,
      );
      this.currentProjectUser.setData(data);
    },

    async deleteProjectUser(projectUserUuid) {
      await rapsotecClient.delete(
        `/project/${this.selectedRapsotecProject.project_id}/users/${projectUserUuid}/`,
      );
    },
    async getDocumentUrl({ idCloud, bimProjectId, idDocument }) {
      const collaborationApi = await getCollaborationApi();
      const { file } = await collaborationApi.getDocument(
        idCloud,
        idDocument,
        bimProjectId,
      );
      return file;
    },
    async getDocumentDetails({ idCloud, bimProjectId, idDocument }) {
      const collaborationApi = await getCollaborationApi();
      const details = await collaborationApi.getDocument(
        idCloud,
        idDocument,
        bimProjectId,
      );
      return details;
    },
    async openDocument({ idCloud, bimProjectId, idDocument }) {
      const collaborationApi = await getCollaborationApi();
      const { file } = await collaborationApi.getDocument(
        idCloud,
        idDocument,
        bimProjectId,
      );
      window.open(file, "_blank");
    },
    async updateRapsotecProject({ projectNewData }) {
      try {
        const { data } = await rapsotecClient.patch(
          `/project/${this.selectedRapsotecProject.project_id}/`,
          projectNewData,
        );
        this.setRapsotecProject(data);
        // INFO - NL - 09/12/2021 - Suppose that mission codes have changed, need to reload questionnaires
        useQuestionnaireStore().questionnairesFetched = false;
      } catch (err) {
        throw new RapsoError(err);
      }
    },
    async getResumeDuplicateProject(requestDatas) {
      try {
        const response = await rapsotecClient.post(
          `/project/${this.selectedRapsotecProject.project_id}/duplicate-datas/get_resume/`,
          requestDatas,
        );
        return response.data;
      } catch (err) {
        throw new RapsoError(
          err.response.data,
          err.response.data.code,
          err.response.data.message,
        );
      }
    },
    async applyDuplicateProject(requestDatas) {
      try {
        const response = await rapsotecClient.post(
          `/project/${this.selectedRapsotecProject.project_id}/duplicate-datas/`,
          requestDatas,
        );
        return response.data;
      } catch (err) {
        throw new RapsoError(
          err.response.data,
          err.response.data.code,
          err.response.data.message,
        );
      }
    },
    async fetchProjectExtra() {
      const { data } = await rapsotecClient.get(
        `/project/${this.selectedRapsotecProject.project_id}/extra/`,
      );
      this.selectedRapsotecProject = new Project({
        ...this.selectedRapsotecProject,
        ...data,
      });
      this.selectedRapsotecProject.extraDataFetched = true;
    },
    async fetchProjectAccessToken() {
      try {
        const { data } = await rapsotecClient.get(
          `project/${this.selectedRapsotecProject.project_id}/bimdata/get_project_access_token/`,
        );
        return data.token;
      } catch (err) {
        this.bimdataState = BIMDATA_STATUS.ERROR;
        throw new RapsoError(
          err?.response?.data,
          err?.response?.data?.code || err?.code,
          err?.response?.data?.message || err?.message,
        );
      }
    },
    async fetchBlueGenToken() {
      try {
        const { data } = await rapsotecClient.get(BLUEGEN_CHATBOT_TOKEN_ENDPOINT);
        return data.access_token;
      } catch (err) {
        throw new RapsoError(
          err?.response?.data,
          err?.response?.data?.code || err?.code,
          err?.response?.data?.message || err?.message,
        );
      }
    },
    async updateSortOrderRVRATTables(params) {
      const {
        tableType,
        tableUuid = null,
        subTableType = null,
        uuids,
      } = params;

      await rapsotecClient.patch(
        `/project/${this.selectedRapsotecProject.project_id}/${tableType}${
          tableUuid && subTableType ? `/${tableUuid}/${subTableType}` : ""
        }/update_sort_order/`,
        uuids,
      );
    },
    async fetchRVRATData() {
      const response = await rapsotecClient.get(
        `/project/${this.selectedRapsotecProject.project_id}/?serializer=rvrat-datas`,
      );
      this.patchRapsotecProject(response.data);
    },
    async fetchJustification(justificationId) {
      const { data } = await rapsotecClient.get(
        `/project/${this.selectedRapsotecProject.project_id}/justifications/${justificationId}/`,
      );
      return data;
    },
    async updateJustification(justificationId, justificationData) {
      const { data } = await rapsotecClient.patch(
        `/project/${this.selectedRapsotecProject.project_id}/justifications/${justificationId}/`,
        justificationData,
      );
      return data;
    },
    async patchProjectData(dataToUpdate) {
      const { data } = await rapsotecClient.patch(
        `/project/${this.selectedRapsotecProject.project_id}/`,
        dataToUpdate,
      );
      this.patchRapsotecProject(data);
    },
    resetProject() {
      this.selectedRapsotecProject = {
        case_number: null,
        address: "",
        city: "",
        webmail_email: "",
        webmail_password: "",
        missions: [],
        entity: "",
      };
      this.unreadMail = 0;
      this.ifcs = null;
      this.projectUsers = [];
      this.currentProjectUser = null;
      this.bimdataProjectToken = null;
      this.bimdataState = "";
    },
    setRapsotecProject(selectedRapsotecProject) {
      this.selectedRapsotecProject = new Project(selectedRapsotecProject);
    },
    patchRapsotecProject(data) {
      this.selectedRapsotecProject = new Project({
        ...this.selectedRapsotecProject,
        ...data,
      });
    },
    setTopicPaneSize(value) {
      this.topicPaneSize = value;
    },

    setBimdataProjectToken(token) {
      this.bimdataProjectToken = token;
    },
    // INFO - AG - 01/06/2022 - Waiting for a response from BimData API
    setBimdataIsLoading() {
      this.bimdataState = BIMDATA_STATUS.LOADING;
    },
    // INFO - AG - 01/06/2022 - BimData token is loaded and use can access every where Bimdata token is necessary
    setBimdataIsLoaded() {
      this.bimdataState = BIMDATA_STATUS.SUCCESS;
    },
  },
});
